
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import breakpoints from "assets/theme/base/breakpoints";
import backgroundImage from "assets/images/bg-sign-in-basic.jpg";
import AuthService from "services/auth-service";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]

function Header({ tabValue, setTabValue }) {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [workspaceProfile, setWorkspaceProfile] = useState({ name: "", profile_image: null, role: "" });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // [NEW] Demo Mode Restriction
  const [showDemoPopup, setShowDemoPopup] = useState(false);

  // [NEW] More Menu State
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const handleMoreMenuOpen = (event) => setMoreMenuAnchor(event.currentTarget);
  const handleMoreMenuClose = () => setMoreMenuAnchor(null);

  const fetchWorkspaceProfile = async () => {
    try {
      const response = await AuthService.getProfile();
      setWorkspaceProfile(response.data.attributes);
    } catch (error) {
      console.error("Error fetching workspace profile:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial
  useEffect(() => {
    fetchWorkspaceProfile();
  }, []);

  // 🔥 Listen for profile updates from Account page
  useEffect(() => {
    const onProfileUpdated = () => fetchWorkspaceProfile();
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  useEffect(() => {
    const handleTabsOrientation = () => {
      setTabsOrientation(
        window.innerWidth < breakpoints.values.sm ? "vertical" : "horizontal"
      );
    };

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();

    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, []);

  const handleProfileClick = (e) => {
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      e.preventDefault(); // Stop the file input from opening
      setShowDemoPopup(true);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    setUploading(true);
    try {
      const response = await AuthService.uploadProfileImage(formData);
      if (response && response.profile_image) {
        // Update local state
        setWorkspaceProfile((prev) => ({ ...prev, profile_image: response.profile_image }));

        // Notify other components (Navbar, Sidebar)
        window.dispatchEvent(new CustomEvent("profile-updated"));
      }
    } catch (error) {
      console.error("Failed to upload profile image:", error);
    } finally {
      setUploading(false);
    }
  };

  if (loading || !workspaceProfile.role) return null;

  const generateBackgroundColor = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 60%, 70%)`;
  };

  const firstLetter = workspaceProfile.name ? workspaceProfile.name.charAt(0).toUpperCase() : "?";
  const avatarBgColor = generateBackgroundColor(workspaceProfile.name || "Anonymous");

  return (
    <MDBox position="relative">
      <MDBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="10.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.info.main, 0),
              rgba(gradients.info.state, 0)
            )}, url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "50%",
          overflow: "hidden",
        }}
      />

      <Card sx={{ position: "relative", mt: -8, mx: 3, py: 2, px: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <MDBox position="relative">
              <input
                type="file"
                id="profile-image-upload"
                hidden
                accept="image/*"
                onChange={handleFileUpload}
              />
              <label htmlFor="profile-image-upload" onClick={handleProfileClick}>
                <MDBox
                  position="relative"
                  sx={{
                    cursor: uploading ? "wait" : "pointer",
                    "&:hover .upload-overlay": {
                      opacity: 1,
                    },
                  }}
                >
                  {workspaceProfile.profile_image ? (
                    <MDAvatar src={workspaceProfile.profile_image} alt="profile-image" size="xl" shadow="sm" />
                  ) : (
                    <MDAvatar
                      sx={{
                        bgcolor: avatarBgColor,
                        color: "white",
                        fontSize: "1.5rem",
                      }}
                      size="xl"
                      shadow="sm"
                    >
                      {firstLetter}
                    </MDAvatar>
                  )}
                  <MDBox
                    className="upload-overlay"
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    bgcolor="rgba(0,0,0,0.4)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="50%"
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    {uploading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <Icon sx={{ color: "white" }}>camera_alt</Icon>
                    )}
                  </MDBox>
                </MDBox>
              </label>
            </MDBox>
          </Grid>

          <Grid item>
            <MDBox height="100%" mt={0.5} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                {workspaceProfile.name || "User"}
              </MDTypography>
              {/* <MDTypography variant="button" color="text" fontWeight="regular">
                {user.role || "Account Owner"}
              </MDTypography> */}
            </MDBox>
          </Grid>

          <Grid item xs={12} md={8} lg={8} sx={{ ml: "auto" }}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={tabValue === 6 || tabValue === 7 ? "more" : tabValue}
                onChange={(event, newValue) => {
                  if (newValue !== "more") {
                    setTabValue(newValue);
                  }
                }}
                sx={{
                  "& .MuiTab-root": {
                    margin: "0 4px",
                    color: "#7b809a !important", // Grey color for inactive tabs
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "#344767 !important", // Dark color for active tab
                  }
                }}
              >
                <Tab label="Account" value={0} icon={<Icon fontSize="small">account_circle</Icon>} />
                <Tab label="Workspaces" value={1} icon={<Icon fontSize="small">work</Icon>} />
                <Tab label="Branding" value={2} icon={<Icon fontSize="small">palette</Icon>} />
                <Tab label="Subscription" value={3} icon={<Icon fontSize="small">credit_card</Icon>} />
                <Tab label="Add-ons" value={4} icon={<Icon fontSize="small">extension</Icon>} />
                <Tab label="Usage" value={5} icon={<Icon fontSize="small">bar_chart</Icon>} />
                {workspaceProfile.role === "Admin" && (
                  <Tab
                    value="more"
                    icon={<Icon fontSize="small" sx={{ mt: 0.5 }}>more_vert</Icon>}
                    sx={{ minWidth: "40px !important", px: 1, mx: 0 }}
                    onClick={handleMoreMenuOpen}
                  />
                )}
              </Tabs>

              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={handleMoreMenuClose}
                elevation={3}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => { setTabValue(6); handleMoreMenuClose(); }} selected={tabValue === 6}>
                  <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>group</Icon>
                  Users
                </MenuItem>
                <MenuItem onClick={() => { setTabValue(7); handleMoreMenuClose(); }} selected={tabValue === 7}>
                  <Icon fontSize="small" sx={{ mr: 1, color: "text.secondary" }}>account_box</Icon>
                  Social Profiles
                </MenuItem>
              </Menu>
            </AppBar>
          </Grid>
        </Grid>
      </Card>

      {/* [NEW] Live Demo Restriction Popup */}
      <LiveDemoRestriction
        open={showDemoPopup}
        onClose={() => setShowDemoPopup(false)}
        featureName={"Updating Profile Picture"}
      />
    </MDBox>
  );
}

Header.propTypes = {
  tabValue: PropTypes.number.isRequired,
  setTabValue: PropTypes.func.isRequired,
};

export default Header;
